<template>
  <div>
    <DxTextBox class="form-field" label="Current Device" :read-only="true" :value="currentDevice" />

    <DxTextBox class="form-field" label="New Device" v-model:value="newDeviceSerial" />

    <DxDateBox class="form-field swap-date" label="Swap Date" type="date" picker-type="calendar"
      :show-clear-button="true" :input-attr="{ 'aria-label': 'Swap Date' }" v-model:value="swapDate" />

    <DxCheckBox class="form-field checkbox" text="Decommission" v-model:value="decommission" />
    <p class="muted hint">
      <template v-if="decommission">
        The site will be taken out of service. Operator and area are left as they are.
      </template>
      <template v-else>
        The site stays in service. Choose where it moves to below &mdash; leave them
        unchanged to keep the current operator and area.
      </template>
    </p>

    <!-- Only relevant when the site remains in service. -->
    <template v-if="!decommission">
      <DxSelectBox class="form-field" label="New Operator" :search-enabled="true" :data-source="operators"
        value-expr="operatorId" display-expr="operatorName" v-model:value="operatorId"
        @value-changed="onOperatorChanged" />

      <DxCheckBox class="area-toggle checkbox" text="View all areas" :value="allAreas"
        :enable-three-state-behavior="false" @value-changed="allAreasChanged" />
      <DxSelectBox class="area-select" label="New Area" :search-enabled="true" :data-source="selectableAreas"
        value-expr="areaId" display-expr="areaName" v-model:value="areaId" />
      <p v-if="operatorId && !selectableAreas.length" class="muted hint">
        This operator has no areas assigned. Tick "View all areas" to pick from every area.
      </p>
    </template>

    <p v-if="error" class="error-msg">{{ error }}</p>

    <br />
    <hr />
    <div class="buttons">
      <DxButton class="saveBtn" icon="save" text="Commit" :disabled="saving" @click="commitSwitch" />
      <DxButton class="cancelBtn" text="Cancel" :disabled="saving" @click="cancelSwitch" />
    </div>
  </div>
</template>

<script>
import DxTextBox from "devextreme-vue/text-box";
import DxSelectBox from "devextreme-vue/select-box";
import DxDateBox from "devextreme-vue/date-box";
import DxButton from "devextreme-vue/button";
import { DxCheckBox } from "devextreme-vue/check-box";

export default {
  name: "DeviceSwitch",
  components: {
    DxTextBox,
    DxSelectBox,
    DxDateBox,
    DxButton,
    DxCheckBox,
  },
  props: {
    // Serial(s) currently on the site, shown read-only.
    currentDevice: { type: String, default: "" },
    // Operator/area the site sits under today; used to preselect the new ones.
    // Named apart from the data properties below -- a prop and a data field
    // cannot share a name.
    initialOperatorId: { type: Number, default: null },
    initialAreaId: { type: Number, default: null },
    // Lookups come from the parent, which has already loaded them.
    operators: { type: Array, default: () => [] },
    areas: { type: Array, default: () => [] },
    operatorAreas: { type: Array, default: () => [] },
    // Bumped by the parent each time the popup opens, so the form resets.
    resetKey: { type: [Number, String], default: 0 },
    saving: { type: Boolean, default: false },
  },
  emits: ["switchCommitted", "switchCancelled"],
  data() {
    return {
      newDeviceSerial: "",
      swapDate: null,
      decommission: false,
      operatorId: null,
      areaId: null,
      allAreas: false,
      error: null,
    };
  },
  created() {
    this.resetForm();
  },
  watch: {
    // The popup keeps this component alive between openings, so the form is
    // cleared whenever the parent signals a fresh open.
    resetKey() {
      this.resetForm();
    },
  },
  computed: {
    // Areas belonging to the chosen operator, matching how the container form
    // itself scopes them. "View all areas" falls back to the full list.
    selectableAreas() {
      if (this.allAreas || !this.operatorId) return this.areas;
      return this.operatorAreas.filter((x) => x.operatorId == this.operatorId);
    },
  },
  methods: {
    resetForm() {
      this.newDeviceSerial = "";
      this.swapDate = null;
      this.decommission = false;
      this.operatorId = this.initialOperatorId ?? null;
      this.areaId = this.initialAreaId ?? null;
      this.allAreas = false;
      this.error = null;
    },
    onOperatorChanged(e) {
      // A held-over area from the previous operator would be an invalid pairing.
      if (e.previousValue !== undefined && e.previousValue !== null) {
        this.areaId = null;
      }
    },
    allAreasChanged(e) {
      this.allAreas = e.value;
      this.areaId = null;
    },
    validate() {
      if (!this.newDeviceSerial || !this.newDeviceSerial.trim()) {
        return "New Device is required.";
      }
      if (!this.swapDate) {
        return "Swap Date is required.";
      }
      if (!this.decommission) {
        if (!this.operatorId) return "New Operator is required.";
        if (!this.areaId) return "New Area is required.";
      }
      return null;
    },
    commitSwitch() {
      this.error = this.validate();
      if (this.error) return;

      this.$emit("switchCommitted", {
        newDeviceSerial: this.newDeviceSerial.trim(),
        swapDate: this.swapDate,
        decommission: this.decommission,
        operatorId: this.decommission ? null : this.operatorId,
        areaId: this.decommission ? null : this.areaId,
      });
    },
    cancelSwitch() {
      this.$emit("switchCancelled");
    },
  },
};
</script>

<style scoped>
.form-field {
  margin: 30px 0 0 0;
}

.checkbox {
  display: block;
}

/* The toggle belongs to the Area box below it, so it keeps the normal gap from
   the Operator box above and almost none from the select it controls. */
.area-toggle {
  display: block;
  margin: 30px 0 0 0;
}

.area-select {
  margin: 4px 0 0 0;
}

/* This theme renders the date box's drop-down with "\f001" -- the same chevron
   as a plain select -- instead of a calendar. \f026 is the DXIcons calendar
   glyph (the one behind .dx-icon-event). */
:deep(.swap-date .dx-dropdowneditor-icon::before) {
  content: "\f026";
}

.hint {
  margin: 8px 0 0 0;
  font-size: 12px;
  line-height: 1.5;
}

.muted {
  color: rgba(150, 150, 150, 0.9);
}

.error-msg {
  margin-top: 16px;
  color: #d9534f;
  font-size: 13px;
}

hr {
  height: 1px;
  border: 1px solid #444;
}

.buttons {
  width: 200px;
  margin: 0 auto;
  display: inline;
}

.saveBtn {
  color: black;
  background-color: rgb(255, 115, 0);
  margin: 0 10px auto;
  /* auto so the label always fits; the floor keeps the pair evenly sized */
  width: auto;
  min-width: 120px;
  display: inline-block;
}

.cancelBtn {
  color: black;
  background-color: rgb(255, 255, 255, 0.3);
  margin: 0 auto;
  width: auto;
  min-width: 120px;
  display: inline-block;
}
</style>
